/*
 * Installing the site as an app ($store.app, used by More → Install App / Uninstall App and the dialogs in
 * layout/dialogs). Chrome, Edge and Samsung Internet install by themselves: their own prompt is left to show as the
 * page opens, and Install App asks for it again. Everywhere else (iPhone and iPad, Firefox, Safari on a Mac) Install
 * App shows that browser's steps, and the same dialog offers them once on a later visit (at most every 30 days after
 * Not Now). Inside the installed app, Uninstall App explains how: a website can't uninstall itself.
 */
const standalone = () => matchMedia('(display-mode: standalone)').matches || navigator.standalone === true;
const agent = navigator.userAgent;
const remember = (key, value) => { try { localStorage.setItem(key, value); } catch (e) {} };
const recall = (key) => { try { return localStorage.getItem(key); } catch (e) { return null; } };

// The device's system (how to uninstall) and the browser on it (how to install)
const os = /iphone|ipad|ipod/i.test(agent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) ? 'ios'
    : /android/i.test(agent) ? 'android'
    : /cros/i.test(agent) ? 'chromeos'
    : /windows/i.test(agent) ? 'windows'
    : /macintosh/i.test(agent) ? 'mac'
    : /linux/i.test(agent) ? 'linux'
    : 'other';
const browser = /samsungbrowser/i.test(agent) ? 'samsung'
    : /edg(a|ios|e)?\//i.test(agent) ? 'edge'
    : /opr\/|opera/i.test(agent) ? 'opera'
    : /firefox|fxios/i.test(agent) ? 'firefox'
    : /chrome|crios|chromium/i.test(agent) ? 'chrome'
    : /safari/i.test(agent) ? 'safari'
    : 'other';
// The steps shown in the install dialog: ios-safari, ios (other iPhone browsers), android-{chrome, samsung,
// firefox, edge, opera, other}, chrome, edge, mac-safari, firefox (on a computer: can't install) or other
const platform = os === 'ios' ? (browser === 'safari' ? 'ios-safari' : 'ios')
    : os === 'android' ? `android-${['chrome', 'samsung', 'firefox', 'edge', 'opera'].includes(browser) ? browser : 'other'}`
    : browser === 'safari' && os === 'mac' ? 'mac-safari'
    : ['chrome', 'edge', 'firefox'].includes(browser) ? browser
    : 'other';

let offer = null;

document.addEventListener('alpine:init', () => {
    Alpine.store('app', {
        installed: standalone(),
        installable: false,     // the browser has its own install prompt ready
        platform,
        os,

        // The browser's own prompt where there is one, else this browser's steps
        async install() {
            if (!offer) {
                return window.dispatchEvent(new CustomEvent('dialog-install', {detail: 'open'}));
            }

            offer.prompt();
            await offer.userChoice;
            offer = null;
            this.installable = false;
        },

        notNow() {
            remember('install-prompt-dismissed', Date.now());
            window.dispatchEvent(new CustomEvent('dialog-install', {detail: 'close'}));
        },
    });

    // Already installed (seen from a browser tab, where the browser can tell)
    navigator.getInstalledRelatedApps?.().then((apps) => apps.length && (Alpine.store('app').installed = true)).catch(() => {});
});

// Held back for the site's own card first (owner, 2026-10-28: as every permission is asked), whose Install opens it
window.addEventListener('beforeinstallprompt', (event) => {
    event.preventDefault();
    offer = event;
    Alpine.store('app').installable = true;
});

window.addEventListener('appinstalled', () => {
    offer = null;
    Alpine.store('app').installable = false;
    Alpine.store('app').installed = true;
});

// The card, from the second visit, a few seconds in: Install where the browser has its prompt, else its steps
window.addEventListener('load', () => {
    const visits = Number(recall('install-visits') || 0) + 1;
    remember('install-visits', visits);

    setTimeout(() => {
        const app = Alpine.store('app');
        const declined = Date.now() - Number(recall('install-prompt-dismissed') || 0) < 30 * 864e5;
        const busy = document.querySelector('dialog[open]');

        if (visits >= 2 && !app.installed && (app.installable || platform !== 'firefox') && !declined && !busy) {
            window.dispatchEvent(new CustomEvent('dialog-install', {detail: 'open'}));
        }
    }, 4000);
});
